import React from "react";
import {
  Ruler,
  Shirt,
  MoveHorizontal,
  Info,
  AlertTriangle,
  Tag,
} from "lucide-react";
import { SizeOption, PlacementInfo } from "../../utils/sizePlacement";

type Props = {
  garmentLabel: string;
  locationLabel: string;
  placementType?: string;
  size: SizeOption;
  placement: PlacementInfo;
  warnings?: string[];
};

function formatPlacementType(value?: string) {
  if (!value) return "Placement";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function SizeResultCard({
  garmentLabel,
  locationLabel,
  placementType,
  size,
  placement,
  warnings = [],
}: Props) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
      {/* HEADER */}
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <Ruler className="h-5 w-5 text-dtf-blue" />
          <h3 className="text-xl font-extrabold tracking-tight text-white">
            Recommended Size & Placement
          </h3>
        </div>

        <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-semibold text-dtf-blue">
          <Tag className="h-3.5 w-3.5" />
          {formatPlacementType(placementType)}
        </div>
      </div>

      {/* GARMENT + SIZE */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-[#08111f] p-4">
          <div className="mb-2 flex items-center gap-2 text-white">
            <Shirt className="h-4 w-4 text-dtf-blue" />
            <span className="font-bold">{garmentLabel}</span>
          </div>
          <p className="text-sm text-dtf-text-muted">{locationLabel}</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#08111f] p-4">
          <p className="text-sm text-dtf-text-muted">Suggested Print Size</p>
          <p className="mt-1 text-3xl font-black tracking-tight text-white">
            {size.width}" × {size.height}"
          </p>
          <p className="mt-1 text-sm font-medium text-dtf-blue">
            {size.label}
          </p>
          <p className="mt-2 text-xs uppercase tracking-[0.18em] text-dtf-text-muted">
            {size.orientation}
          </p>
        </div>
      </div>

      {/* BEST FOR */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-[#08111f] p-4">
        <p className="text-sm font-bold text-white">Best For</p>

        <div className="mt-3 flex flex-wrap gap-2">
          {size.recommendedFor?.length ? (
            size.recommendedFor.map((item) => (
              <span
                key={item}
                className="rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-medium text-dtf-blue"
              >
                {item}
              </span>
            ))
          ) : (
            <span className="text-sm text-dtf-text-muted">
              General DTF placement use
            </span>
          )}
        </div>
      </div>

      {/* PLACEMENT DETAILS */}
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-[#08111f] p-4">
          <div className="mb-2 flex items-center gap-2 text-dtf-blue">
            <MoveHorizontal className="h-4 w-4" />
            <span className="text-sm font-bold text-white">From Collar</span>
          </div>
          <p className="text-sm leading-6 text-dtf-text">
            {placement.fromCollar}
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#08111f] p-4">
          <div className="mb-2 flex items-center gap-2 text-dtf-blue">
            <MoveHorizontal className="h-4 w-4" />
            <span className="text-sm font-bold text-white">
              Horizontal Placement
            </span>
          </div>
          <p className="text-sm leading-6 text-dtf-text">
            {placement.horizontal}
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#08111f] p-4">
          <div className="mb-2 flex items-center gap-2 text-dtf-blue">
            <Info className="h-4 w-4" />
            <span className="text-sm font-bold text-white">Notes</span>
          </div>
          <p className="text-sm leading-6 text-dtf-text">
            {placement.notes}
          </p>
        </div>
      </div>

      {/* WARNINGS */}
      {warnings.length > 0 && (
        <div className="mt-4 rounded-2xl border border-dtf-accent/20 bg-dtf-accent/10 p-4">
          <div className="mb-2 flex items-center gap-2 text-dtf-accent">
            <AlertTriangle className="h-4 w-4" />
            <p className="text-sm font-bold">Watch Out For</p>
          </div>

          <div className="space-y-2">
            {warnings.map((warning) => (
              <p key={warning} className="text-sm leading-6 text-dtf-text">
                • {warning}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* MOCKUP CTA */}
      <div className="mt-4 rounded-2xl border border-dtf-blue/20 bg-dtf-blue/10 p-4">
        <p className="text-sm font-bold text-dtf-blue">
          Want to Preview It on a Garment?
        </p>
        <p className="mt-1 text-sm leading-6 text-dtf-text">
          Use the mockup builder to visually test placement and see how your
          design looks on the garment.
        </p>

        <a
          href="/mockup-builder"
          className="mt-3 inline-flex text-sm font-semibold text-dtf-blue hover:underline"
        >
          Open Mockup Builder →
        </a>
      </div>
    </div>
  );
}